import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Minus, Plus, Tag, Trash2 } from "lucide-react";
import { brl } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const COUPONS = { TIME10: 0.1, MAGE15: 0.15 };
const SERVICE_FEE = 0.99;

export default function Cart() {
  const { cart, setQty, subtotal, clear } = useCart();
  const navigate = useNavigate();
  const [coupon, setCoupon] = useState(localStorage.getItem("gf_coupon") || "");
  const [applied, setApplied] = useState(localStorage.getItem("gf_coupon") || "");

  const deliveryFee = cart.store?.delivery_fee || 0;
  const discount = applied ? subtotal * (COUPONS[applied] || 0) : 0;
  const total = subtotal - discount + (cart.items.length ? deliveryFee + SERVICE_FEE : 0);

  const apply = () => {
    const c = coupon.toUpperCase().trim();
    if (COUPONS[c]) {
      setApplied(c);
      localStorage.setItem("gf_coupon", c);
      toast.success(`Cupom ${c} aplicado!`);
    } else {
      toast.error("Cupom inválido. Tente TIME10 ou MAGE15.");
    }
  };

  return (
    <div data-testid="cart-page" className="pb-28">
      <header className="sticky top-0 z-30 bg-white border-b border-gray-200 px-4 py-3 flex items-center gap-3">
        <button onClick={() => navigate(-1)} data-testid="cart-back-button" className="p-1 gf-press">
          <ArrowLeft size={20} />
        </button>
        <h1 className="font-display font-extrabold text-lg">Seu carrinho</h1>
      </header>

      {cart.items.length === 0 ? (
        <div className="p-8 text-center" data-testid="cart-empty">
          <p className="text-gray-500 text-sm">Seu carrinho está vazio.</p>
          <Link to="/" className="inline-block mt-4 font-bold text-gf-orange">Ver restaurantes</Link>
        </div>
      ) : (
        <>
          <div className="px-4 pt-4">
            <p className="text-xs text-gray-500">Pedido em</p>
            <p className="font-display font-bold text-gf-black" data-testid="cart-store-name">{cart.store.name}</p>
          </div>

          <div className="px-4 mt-3 grid gap-2">
            {cart.items.map((i) => (
              <div key={i.id} className="flex items-center gap-3 p-3 rounded-2xl border border-gray-200">
                <img src={i.image_url} alt={i.name} className="w-14 h-14 rounded-xl object-cover" />
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-sm truncate">{i.name}</p>
                  <p className="text-xs text-gf-orange font-bold">{brl(i.price * i.qty)}</p>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    data-testid={`cart-decrease-${i.id}`}
                    onClick={() => setQty(i.id, i.qty - 1)}
                    className="w-8 h-8 rounded-lg border border-gray-300 flex items-center justify-center gf-press"
                  >
                    {i.qty === 1 ? <Trash2 size={14} className="text-mage-red" /> : <Minus size={14} />}
                  </button>
                  <span data-testid={`cart-qty-${i.id}`} className="w-5 text-center font-bold text-sm">{i.qty}</span>
                  <button
                    data-testid={`cart-increase-${i.id}`}
                    onClick={() => setQty(i.id, i.qty + 1)}
                    className="w-8 h-8 rounded-lg gf-gradient text-white flex items-center justify-center gf-press"
                  >
                    <Plus size={14} />
                  </button>
                </div>
              </div>
            ))}
          </div>

          <div className="px-4 mt-5">
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Tag size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <Input
                  data-testid="coupon-input"
                  value={coupon}
                  onChange={(e) => setCoupon(e.target.value)}
                  placeholder="Cupom de desconto"
                  className="h-11 pl-9 rounded-xl"
                />
              </div>
              <Button data-testid="apply-coupon-button" onClick={apply} variant="outline" className="h-11 rounded-xl font-bold">
                Aplicar
              </Button>
            </div>
          </div>

          <div className="px-4 mt-5 space-y-2 text-sm">
            <Row label="Subtotal" value={brl(subtotal)} testId="cart-subtotal" />
            {applied && <Row label={`Desconto (${applied})`} value={`- ${brl(discount)}`} testId="cart-discount" green />}
            <Row label="Taxa de entrega" value={brl(deliveryFee)} testId="cart-delivery-fee" />
            <Row label="Taxa de serviço" value={brl(SERVICE_FEE)} testId="cart-service-fee" />
            <div className="flex justify-between pt-3 border-t border-dashed">
              <span className="font-display font-extrabold text-base">Total</span>
              <span data-testid="cart-total" className="font-display font-extrabold text-lg text-gf-orange">{brl(total)}</span>
            </div>
          </div>

          <div className="fixed bottom-[76px] left-1/2 -translate-x-1/2 w-[calc(100%-32px)] max-w-[488px] z-40">
            <Button
              data-testid="checkout-button"
              onClick={() => navigate("/checkout")}
              className="w-full h-13 py-3.5 rounded-2xl gf-gradient text-white font-display font-extrabold text-base gf-press shadow-xl"
            >
              Finalizar pedido · {brl(total)}
            </Button>
          </div>
        </>
      )}
    </div>
  );
}

const Row = ({ label, value, testId, green }) => (
  <div className="flex justify-between">
    <span className="text-gray-600">{label}</span>
    <span data-testid={testId} className={`font-semibold ${green ? "text-mage-green" : ""}`}>{value}</span>
  </div>
);
